// src/components/MessagesTab.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import AddIcon from '@mui/icons-material/Add';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import ComposeDialog from '../email/ComposeDialog';
import axios from 'axios';
import EmailListContainer from '../email/EmailListContainer';
import EmailDetails from '../email/EmailDetails';
import { useSocket } from '../../SocketContext';
import { useLocation } from 'react-router-dom';
import { acme } from '../../theme/acmeTheme';
import { Card, CardContent, Button as AcmeButton, IconButton as AcmeIconButton } from '../ui';

const selectAcmeSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: acme.colors.border },
    '&:hover fieldset': { borderColor: acme.colors.brand },
    '&.Mui-focused fieldset': { borderColor: acme.colors.brand },
  },
};

const outlineActionSx = {
  borderColor: acme.colors.border,
  color: acme.colors.foreground,
  fontWeight: 600,
  borderRadius: 2,
  '&:hover': {
    borderColor: acme.colors.brand,
    color: acme.colors.brand,
    backgroundColor: acme.colors.brandMuted,
  },
  transition: 'all 0.2s ease',
};

function MessagesTab() {
  const location = useLocation();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('INBOX');
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const selectedEmailRef = useRef(null);
  selectedEmailRef.current = selectedEmail;

  // Compose / Reply
  const [openCompose, setOpenCompose] = useState(false);
  const [openReply, setOpenReply] = useState(false);
  const [replyTo, setReplyTo] = useState('');
  const [replySubject, setReplySubject] = useState('');

  // Notifications
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);

  const socket = useSocket();

  // Функция загрузки папок
  const loadFolders = useCallback(async () => {
    if (!selectedAccount) return;
    
    try {
      setLoadingFolders(true);
      console.log('📁 [MessagesTab] Loading folders for account:', selectedAccount.email);
      
      const res = await axios.get('/api/mail/folders', {
        params: { accountId: selectedAccount._id },
        withCredentials: true,
      });
      
      console.log('📁 [MessagesTab] Loaded folders:', res.data);
      const folderList = res.data || [];
      setFolders(folderList);

      // Если текущая выбранная папка не существует в списке, выбираем первую доступную
      const folderNames = folderList.map((f) => f.fullPath);
      if (!folderNames.includes(selectedFolder)) {
        // Ищем INBOX в разных вариантах
        const inboxFolder =
          folderNames.find((name) => name.toLowerCase() === 'inbox') ||
          folderNames[0];
        
        if (inboxFolder) {
          setSelectedFolder(inboxFolder);
        }
      }
    } catch (error) {
      console.error('❌ [MessagesTab] Error loading folders:', error);
      setErrorMessage('Failed to load folders. Using default folders.');
      // Fallback к стандартным папкам
      setFolders([
        { fullPath: 'INBOX', displayName: 'Inbox', name: 'INBOX' },
        { fullPath: 'Sent', displayName: 'Sent', name: 'Sent' },
        { fullPath: 'Drafts', displayName: 'Drafts', name: 'Drafts' },
        { fullPath: 'Trash', displayName: 'Trash', name: 'Trash' }
      ]);
    } finally {
      setLoadingFolders(false);
    }
  }, [selectedAccount, selectedFolder, setErrorMessage]);

  // ??????? ???????? ?????
  const loadEmails = useCallback(async () => {
    console.log('🔄 [MessagesTab] loadEmails() function triggered');
    if (!selectedAccount) {
      console.log('⚠️ [MessagesTab] No selectedAccount set');
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get('/api/mail/messages', {
        params: { accountId: selectedAccount._id, folder: selectedFolder },
        withCredentials: true,
      });
      const sorted = res.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      console.log(`✅ [MessagesTab] Loaded ${sorted.length} emails`);
      setEmails(sorted);
      const current = selectedEmailRef.current;
      if (current) {
        const matched = sorted.find((email) => email.uid === current.uid);
        if (matched) {
          setSelectedEmail(matched);
        }
      }
    } catch (error) {
      console.error('❌ [MessagesTab] Error loading emails:', error);
      // Показываем пользователю ошибку при неудачной загрузке
      if (error.response && error.response.status === 500) {
        setErrorMessage(`Failed to load emails from ${selectedFolder}. This folder might not exist for this account.`);
      } else {
        setErrorMessage('Failed to load emails. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, selectedFolder, setErrorMessage]);

  // ?????????? ??????????? ??????? ??? ?????????? ????? (????????, ???????? refresh)
  useEffect(() => {
    const handleRefreshEmails = () => {
      console.log('🔄 [MessagesTab] refreshEmails event triggered');
      loadEmails();
    };

    window.addEventListener('refreshEmails', handleRefreshEmails);

    return () => {
      window.removeEventListener('refreshEmails', handleRefreshEmails);
    };
  }, [loadEmails]);

  // ????????? ?????????
  useEffect(() => {
    async function fetchAccounts() {
      try {
        const res = await axios.get('/api/mail/accounts', { withCredentials: true });
        setAccounts(res.data || []);
        if (res.data && res.data.length > 0) {
          setSelectedAccount(res.data[0]);
          console.log('✅ [MessagesTab] Account selected:', res.data[0]);
        } else {
          console.log('⚠️ [MessagesTab] No accounts found');
        }
      } catch (error) {
        console.error('❌ [MessagesTab] Error fetching accounts:', error);
      }
    }
    fetchAccounts();
  }, []);

  // Загружаем папки при изменении аккаунта
  useEffect(() => {
    if (selectedAccount) {
      loadFolders();
    }
  }, [selectedAccount, loadFolders]);

  // ???????? ????? ? ???????? ?? ??????? "notificationCreated"
  useEffect(() => {
    if (!selectedAccount) {
      console.log('⚠️ [MessagesTab] No account selected, skipping email loading');
      return;
    }
    
    // ????????? ?????? ????? ??? ????????? ???????? ??? ?????
    console.log('🔄 [MessagesTab] Initial email loading');
    loadEmails();

    if (!socket) {
      console.log('⚠️ [MessagesTab] No socket available, skipping socket event registration');
      return;
    }

    console.log('✅ [MessagesTab] Setting up socket notification listener');
    
    const notificationHandler = (notification) => {
      console.log('📨 [MessagesTab] notificationCreated event received:', notification);
      
      if (notification && notification.type === 'newEmail') {
        console.log('✅ [MessagesTab] Loading emails after email notification');
        loadEmails();
      } else {
        console.log('ℹ️ [MessagesTab] Ignoring non-email notification');
      }
    };

    socket.on('notificationCreated', notificationHandler);

    // Устанавливаем интервал для регулярного обновления писем
    const intervalId = setInterval(() => {
      console.log('🔄 [MessagesTab] Periodic email refresh');
      loadEmails();
    }, 60000); // Каждую минуту

    return () => {
      socket.off('notificationCreated', notificationHandler);
      clearInterval(intervalId);
    };
  }, [socket, loadEmails, selectedAccount, selectedFolder]);
  
   useEffect(() => {
    if (location.state && location.state.selectedMessage) {
      setSelectedEmail(location.state.selectedMessage);
    }
  }, [location.state]);

  const handleNewMessage = () => {
    setOpenCompose(true);
  };

  const handleOpenReply = (fromAddress, subjectValue) => {
    setReplyTo(fromAddress || '');
    setReplySubject(subjectValue ? `Re: ${subjectValue}` : '');
    setOpenReply(true);
  };

  const handleDelete = () => {
    if (!selectedEmail || !selectedAccount) return;
    axios
      .post(`/api/mail/messages/${selectedAccount._id}/move`, {
        messageSeqno: selectedEmail.seqno.toString(),
        sourceFolder: selectedFolder,
        targetFolder: 'Trash'
      }, { withCredentials: true })
      .then(() => {
        // Перезагружаем emails после удаления
        loadEmails();
        setSelectedEmail(null);
        setSuccessMessage('Email successfully deleted');
      })
      .catch((err) => {
        console.error('Delete error:', err);
        setErrorMessage('Failed deleting email');
      });
  };

  const handleRelocate = (targetFolder) => {
    if (!selectedEmail || !selectedAccount) return;
    axios
      .post(`/api/mail/messages/${selectedAccount._id}/move`, {
        messageSeqno: selectedEmail.seqno.toString(),
        sourceFolder: selectedFolder,
        targetFolder
      }, { withCredentials: true })
      .then(() => {
        // Перезагружаем emails после перемещения
        loadEmails();
        setSelectedEmail(null);
        setSuccessMessage('Email successfully moved');
      })
      .catch((err) => {
        console.error('Relocate error:', err);
        setErrorMessage('Failed moving email');
      });
  };

  const handleSelectEmail = (email) => {
    if (!selectedAccount) return;
    const updatedEmail = { ...email, unread: false };
    setSelectedEmail(updatedEmail);
    setEmails((prevEmails) =>
      prevEmails.map((e) => (e.seqno === email.seqno ? updatedEmail : e))
    );
    axios.post(`/api/mail/markAsRead/${selectedAccount._id}`, {
      messageUid: email.uid,
      folder: selectedFolder,
    }, { withCredentials: true })
    .catch((err) => console.error('markAsRead error:', err));
  };

  return (
    <Box sx={{ 
      backgroundColor: acme.colors.pageBg, 
      p: 3, 
      height: 'calc(100vh - 100px)',
    }}>
      
      <PageSnackbars
        {...snackbarProps}
        successAlertSx={{
          borderRadius: 2,
          '& .MuiAlert-icon': {
            color: acme.colors.brand
          }
        }}
        errorAlertSx={{ borderRadius: 2 }}
      />

      <Card
        sx={{
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'none',
        }}
      >
        <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 0 } }}>
        {/* Toolbar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          backgroundColor: acme.colors.muted,
          borderRadius: 2,
          border: `1px solid ${acme.colors.border}`,
          flexWrap: 'wrap',
          gap: 1,
        }}>
          <Select
            size="small"
            value={selectedAccount ? selectedAccount._id : ''}
            onChange={(e) => {
              const acc = accounts.find((a) => a._id === e.target.value);
              setSelectedAccount(acc);
            }}
            sx={{ mr: 2, minWidth: 200, ...selectAcmeSx }}
          >
            {accounts.map((acc) => (
              <MenuItem key={acc._id} value={acc._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    backgroundColor: acc.status === 'Active' ? acme.colors.brand : acme.colors.destructive 
                  }} />
                  {acc.email}
                </Box>
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={selectedFolder}
            onChange={(e) => setSelectedFolder(e.target.value)}
            sx={{ mr: 2, minWidth: 140, ...selectAcmeSx }}
            disabled={loadingFolders}
          >
            {loadingFolders ? (
              <MenuItem value="">
                <CircularProgress size={16} sx={{ mr: 1, color: acme.colors.brand }} />
                Loading folders...
              </MenuItem>
            ) : (
              folders.map((f) => (
                <MenuItem key={f.fullPath} value={f.fullPath}>
                  {f.displayName}
                </MenuItem>
              ))
            )}
          </Select>
          <Divider orientation="vertical" flexItem sx={{ mx: 2, borderColor: acme.colors.border }} />
          <Button
            variant="outlined"
            onClick={loadEmails}
            sx={{ mr: 2, ...outlineActionSx }}
            startIcon={loading ? <CircularProgress size={18} sx={{ color: acme.colors.brand }} /> : null}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update mail'}
          </Button>
          <AcmeButton
            variant="default"
            startIcon={<AddIcon />}
            onClick={handleNewMessage}
            sx={{ mr: 2 }}
          >
            New message
          </AcmeButton>
          <Button
            variant="outlined"
            startIcon={<ReplyIcon />}
            onClick={() =>
              handleOpenReply(selectedEmail?.from, selectedEmail?.subject)
            }
            disabled={!selectedEmail}
            sx={{ mr: 2, ...outlineActionSx, '&:disabled': { borderColor: acme.colors.border, color: acme.colors.mutedForegroundSolid } }}
          >
            Reply
          </Button>
          <AcmeIconButton 
            onClick={handleDelete} 
            disabled={!selectedEmail} 
            sx={{ ml: 2, color: acme.colors.destructive }}
          >
            <DeleteIcon fontSize="small" />
          </AcmeIconButton>
        </Box>
        <Divider sx={{ borderColor: acme.colors.border }} />

        {loading && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 2, 
            ml: 2,
            p: 2,
            backgroundColor: acme.colors.brandMuted,
            borderRadius: 2,
            border: `1px solid ${acme.colors.border}`
          }}>
            <CircularProgress size={20} sx={{ mr: 2, color: acme.colors.brand }} />
            <Typography variant="body2" sx={{ color: acme.colors.foreground, fontWeight: 500 }}>
              Loading emails...
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden', mt: 2, flex: 1 }}>
          <Box
            sx={{
              width: 420,
              minWidth: 420,
              borderRight: `1px solid ${acme.colors.border}`,
              mr: 2,
              overflowY: 'auto',
              pr: 2,
            }}
          >
            <EmailListContainer
              emails={emails}
              onSelectEmail={handleSelectEmail}
              selectedEmail={selectedEmail}
            />
          </Box>
          {/* ?????? ?????? */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            pl: 2,
          }}>
            {selectedEmail ? (
              <EmailDetails
                email={selectedEmail}
                accountId={selectedAccount ? selectedAccount._id : null}
                onReply={handleOpenReply}
                onDelete={handleDelete}
                onRelocate={handleRelocate}
                availableFolders={folders}
              />
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                p: 4
              }}>
                <Box sx={{ fontSize: '4rem', mb: 2, opacity: 0.3, color: acme.colors.brand }}>
                  📧
                </Box>
                <Typography variant="h6" sx={{ mb: 1, color: acme.colors.foreground, fontWeight: 600 }}>
                  Select an email to view
                </Typography>
                <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, maxWidth: 300 }}>
                  Choose a message from the list to read its content and manage attachments
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        </CardContent>
      </Card>

      {/* ?????? "New message" */}
      {selectedAccount && openCompose && (
        <ComposeDialog
          open={openCompose}
          onClose={() => setOpenCompose(false)}
          onSuccess={(msg) => setSuccessMessage(msg)}
          mode="new"
          accountId={selectedAccount._id}
          defaultTo=""
        />
      )}
      {/* ?????? "Reply" */}
      {selectedAccount && openReply && (
        <ComposeDialog
          open={openReply}
          onClose={() => setOpenReply(false)}
          onSuccess={(msg) => setSuccessMessage(msg)}
          mode="reply"
          accountId={selectedAccount._id}
          defaultTo={replyTo}
          defaultSubject={replySubject}
        />
      )}
    </Box>
  );
}

export default MessagesTab;
